import "./Assessment.css";
import { Fragment, useEffect, useState } from "react";
import { Content, Header, Main } from "../../commons/Template/Template";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../../api/resources";
import { FormLoader } from "../../commons/Form/Form";
import routes from "../../../routes/routes";
import { useModalDispatch } from "../../commons/Modal/ModalContext";
import Results from "./Results/Results";
import { useAuth } from "../../../auth/AuthContext";
import { FinancialData, FinancialDataKeys } from "../../Onboarding/Onboarding";
import { OnboardingProvider } from "../../Onboarding/OnboardingContext";
import Button from "../../commons/Button/Button";
import Typo from "../../commons/Typo/Typo";

export default function Assessment() {
  // Flow
  const goForward = (need, results) => {
    const to = routes.Financing.unlock;
    const state = {
      need,
      results,
    };
    navigate(to, { state });
  };

  const { t } = useTranslation(["common", "financing"]);
  document.title = t("financing:assessment.title");
  const navigate = useNavigate();
  const dispatchModal = useModalDispatch();

  const [loading, setLoading] = useState(true);

  const { activeCompany, loadingActiveCompany } = useAuth();

  const { results, setResults } = useResults(null);

  const needs = [
    "accounts_receivable",
    "long_term",
    "short_term",
    "inventory",
    "accounts_payable",
  ];

  const noFinancialData = (company) =>
    FinancialDataKeys.every((key) => company[key] === null);

  const init = async () => {
    if (noFinancialData(activeCompany)) {
      const emptyResults = Object.fromEntries(
        needs.map((need) => [need, null]),
      );

      setResults(emptyResults);
      setLoading(false);
      return;
    }

    const response = await api.assessActiveCompany();
    if (!response.ok) {
      return;
    }

    const jsonResults = await response.json();
    setResults(jsonResults);
    setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  const handleNoFinancialData = () => {
    dispatchModal({
      type: "add",
      className: "FinancialDataModal",
      title: t("onboarding:forms.FinancialData.title"),
      component: (
        <FinancialDataModal
          onAssess={(assessmentResults) => {
            setResults(assessmentResults);
            window.location.reload();
            // init();
            // dispatchModal({type: "success"})
          }}
        />
      ),
    });
  };

  return (
    <Main themeSwitch>
      <Header>
        <Typo variant="h1">{t("financing:assessment.title")}</Typo>
        <Typo variant="body2">{t("financing:assessment.subheading")}</Typo>
      </Header>
      <Content>
        {loading || loadingActiveCompany ? (
          <FormLoader />
        ) : (
          <Fragment>
            <Results
              results={results}
              onClick={
                noFinancialData(activeCompany)
                  ? handleNoFinancialData
                  : (need) => goForward(need, results)
              }
            />
            {!noFinancialData(activeCompany) && (
              <FinancialDataFormRestart onClick={handleNoFinancialData} />
            )}
          </Fragment>
        )}
      </Content>
    </Main>
  );
}

function FinancialDataFormRestart({ onClick }) {
  const { t } = useTranslation(["common", "financing"]);
  return (
    <div className="Results-after">
      <div className="Results-after-text">
        <Typo className="Results-after-text-title">
          {t("financing:assessment.results.restart.title")}
        </Typo>
        <Typo className="Results-after-text-subtitle">
          {t("financing:assessment.results.restart.subtitle")}
        </Typo>
      </div>

      <div className="Results-after-buttons">
        <Button
          text={t("financing:assessment.results.restart.buttons.restart")}
          variant="secondary_b"
          onClick={onClick}
        />
      </div>
    </div>
  );
}

function FinancialDataModal({ onAssess }) {
  const { activeCompany, loadingActiveCompany } = useAuth();

  if (loadingActiveCompany) return <FormLoader />;
  return (
    <OnboardingProvider
      assessment
      onAssess={onAssess}
      activeCompany={activeCompany}
    >
      <div style={{ paddingTop: "0.5rem" }}>
        <FinancialData />
      </div>
    </OnboardingProvider>
  );
}

export function useResults(
  init = {
    long_term: {
      Defacto: true,
    },
    accounts_receivable: {
      Defacto: true,
    },
    short_term: {
      Defacto: true,
      Karmen: true,
      Avanseo: true,
    },
    inventory: {
      Defacto: true,
      Karmen: true,
      Avanseo: true,
    },
    accounts_payable: {
      Avanseo: true,
    },
  },
) {
  const [results, setResults] = useState(init);
  return { results, setResults };
}
